import {
  CreateBulkPdfExportRequestDTOSchema,
  CreateCsvExportRequestDTOSchema,
  CreatePdfExportRequestDTOSchema,
  CreateRegisterPdfExportRequestDTOSchema,
} from '@gearbelt/global';
import { z } from 'zod';

const STORAGE_PREFIX = 'gearbelt:export-job:';
const STALE_AFTER_MS = 4 * 60 * 60 * 1000;

export const EXPORT_PROGRESS_ROUTE = '/exports/progress';
export const EXPORT_JOB_PARAM = 'job';

export const ExportJobSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('csv'), request: CreateCsvExportRequestDTOSchema }),
  z.object({ kind: z.literal('pdf'), request: CreatePdfExportRequestDTOSchema }),
  z.object({ kind: z.literal('pdf-bulk'), request: CreateBulkPdfExportRequestDTOSchema }),
  z.object({ kind: z.literal('pdf-scoped'), request: CreateRegisterPdfExportRequestDTOSchema }),
]);
export type ExportJob = z.infer<typeof ExportJobSchema>;

const StoredExportJobSchema = z.object({
  createdAt: z.number(),
  job: ExportJobSchema,
});

function pruneStaleJobs() {
  try {
    const now = Date.now();
    for (let index = window.localStorage.length - 1; index >= 0; index -= 1) {
      const key = window.localStorage.key(index);
      if (!key?.startsWith(STORAGE_PREFIX)) continue;
      const raw = window.localStorage.getItem(key);
      const parsed = raw ? StoredExportJobSchema.safeParse(JSON.parse(raw)) : null;
      if (!parsed?.success || now - parsed.data.createdAt > STALE_AFTER_MS) {
        window.localStorage.removeItem(key);
      }
    }
  } catch {
    // A single malformed entry or a quota/JSON error must not block opening the tab.
  }
}

/**
 * Hands a server-side export off to a dedicated progress tab (Dashpivot's pattern):
 * the descriptor is stored under a random key so arbitrarily large filter id lists
 * survive without overflowing the query string, then the tab is opened synchronously
 * (no `await` before `window.open`, so the browser attributes it to the user gesture
 * and doesn't block the popup). The new tab owns the create → poll → download lifecycle.
 */
export function openExportTab(job: ExportJob) {
  const key = `${STORAGE_PREFIX}${crypto.randomUUID()}`;
  pruneStaleJobs();
  try {
    window.localStorage.setItem(key, JSON.stringify({ createdAt: Date.now(), job }));
  } catch {
    // Fall through and still open the tab; the reader surfaces the missing job as an error.
  }
  const params = new URLSearchParams({ [EXPORT_JOB_PARAM]: key });
  window.open(`${EXPORT_PROGRESS_ROUTE}?${params.toString()}`, '_blank');
}

// The progress tab reads the job from a lazy `useState` initializer, which React
// re-invokes under StrictMode (double-invoke + mount→unmount→mount). Memoise the
// resolved job by key so removing the backing entry on first read stays idempotent
// across those repeat calls instead of resolving to null on the second one.
const consumedJobs = new Map<string, ExportJob>();

export function readExportJob(key: string | null): ExportJob | null {
  if (!key?.startsWith(STORAGE_PREFIX)) return null;
  const cached = consumedJobs.get(key);
  if (cached) return cached;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = StoredExportJobSchema.safeParse(JSON.parse(raw));
    if (!parsed.success || Date.now() - parsed.data.createdAt > STALE_AFTER_MS) return null;
    consumedJobs.set(key, parsed.data.job);
    try {
      window.localStorage.removeItem(key);
    } catch {
      // Best-effort cleanup; pruneStaleJobs removes it on the next tab open.
    }
    return parsed.data.job;
  } catch {
    return null;
  }
}
